import { Button, Container, Drawer, IconButton } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { useAppSelector } from "../../../app/hooks";
import { useGetCurrentUserQuery } from "../../../app/user/api";
import logo4Img from "../../../assets/svg/aidentis_4.svg";
import logo4ImgWhite from "../../../assets/svg/aidentis_4_white.svg";
import AddLeftCasesModal from "../../../components/AddLeftCasesModal";
import CreatePromocodeModal from "../../../components/CreatePromocodeModal";
import LangSwitcher from "../../../components/LangSwitcher";
import ThemeSwitcher from "../../../components/ThemeSwitcher";
import { useThemeContext } from "../../../context/ThemeContext";
import PATH from "../../../helpers/PATH";
import { AppHeaderWrapper, MobileMenuContent } from "./styles";

interface AppHeaderProps {
  isPanoramicResults?: boolean;
}

const AppHeader: React.FC<AppHeaderProps> = ({
  isPanoramicResults = false,
}) => {
  const { t } = useTranslation();
  const { mode } = useThemeContext();
  const { token, user } = useAppSelector((state) => state.userSlice);
  const navigate = useNavigate();
  const [isPromocodeModalOpen, setIsPromocodeModalOpen] = useState(false);
  const [isAddLeftCasesModalOpen, setIsAddLeftCasesModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Fetch current user data when token is available
  useGetCurrentUserQuery(null, { skip: !token });

  // Add a fallback to show "My Profile" if user data is not yet loaded
  const displayName = user?.firstName || t("app:myProfile");

  // Check if user is admin
  const isAdmin =
    user?.roles?.some((role) => role.name === "ROLE_ADMIN") || false;

  const handleMobileMenuClose = () => {
    setIsMobileMenuOpen(false);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    handleMobileMenuClose();
  };

  return (
    <AppHeaderWrapper isPanoramicResults={isPanoramicResults} mode={mode}>
      <Container
        sx={{
          padding: `${isPanoramicResults ? "0 130px" : "0"} !important`,
          maxWidth: `${isPanoramicResults ? "100%" : "1468px"} !important`,
        }}
      >
        <div className="header">
          <Link className="logo" to={token ? PATH.APP_CASES : PATH.HOME}>
            <img
              alt="AIDentis"
              src={mode === "dark" ? logo4ImgWhite : logo4Img}
            />
          </Link>

          {/* Desktop Menu */}
          <menu className="desktop-menu">
            <li>
              <ThemeSwitcher />
            </li>
            {token ? (
              <>
                {isAdmin && (
                  <>
                    <li>
                      <Button
                        sx={{ color: mode === "dark" ? "#F0F0F0" : "#1A509B" }}
                        onClick={() => setIsPromocodeModalOpen(true)}
                      >
                        Создать промокод
                      </Button>
                    </li>
                    <li>
                      <Button
                        sx={{ color: mode === "dark" ? "#F0F0F0" : "#1A509B" }}
                        onClick={() => setIsAddLeftCasesModalOpen(true)}
                      >
                        Добавить кейсы
                      </Button>
                    </li>
                  </>
                )}
                <li>
                  <Button
                    sx={{ color: mode === "dark" ? "#F0F0F0" : "#1A509B" }}
                    onClick={() => navigate(PATH.APP_PROFILE)}
                  >
                    {displayName}
                  </Button>
                </li>
              </>
            ) : null}
            <li>
              <LangSwitcher />
            </li>
          </menu>

          {/* Mobile Controls */}
          <div className="mobile-controls">
            <ThemeSwitcher />
            <IconButton
              className="burger-btn"
              onClick={() => setIsMobileMenuOpen(true)}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 12H21M3 6H21M3 18H21"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </IconButton>
          </div>
        </div>
      </Container>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={isMobileMenuOpen}
        onClose={handleMobileMenuClose}
        PaperProps={{
          sx: {
            backgroundColor: mode === "dark" ? "#1F242A" : "#ffffff",
          },
        }}
      >
        <MobileMenuContent mode={mode}>
          <div className="menu-header">
            <Link
              className="logo"
              to={token ? PATH.APP_CASES : PATH.HOME}
              onClick={handleMobileMenuClose}
            >
              <img
                alt="AIDentis"
                src={mode === "dark" ? logo4ImgWhite : logo4Img}
              />
            </Link>
            <IconButton className="close-btn" onClick={handleMobileMenuClose}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </IconButton>
          </div>

          <ul className="menu-list">
            {token && (
              <>
                <li>
                  <Button onClick={() => handleNavigate(PATH.APP_PROFILE)}>
                    {displayName}
                  </Button>
                </li>
                {isAdmin && (
                  <>
                    <li>
                      <Button
                        onClick={() => {
                          setIsPromocodeModalOpen(true);
                          handleMobileMenuClose();
                        }}
                      >
                        Создать промокод
                      </Button>
                    </li>
                    <li>
                      <Button
                        onClick={() => {
                          setIsAddLeftCasesModalOpen(true);
                          handleMobileMenuClose();
                        }}
                      >
                        Добавить кейсы
                      </Button>
                    </li>
                  </>
                )}
              </>
            )}
          </ul>

          <div className="menu-footer">
            <LangSwitcher />
          </div>
        </MobileMenuContent>
      </Drawer>

      <CreatePromocodeModal
        open={isPromocodeModalOpen}
        onClose={() => setIsPromocodeModalOpen(false)}
      />
      <AddLeftCasesModal
        open={isAddLeftCasesModalOpen}
        onClose={() => setIsAddLeftCasesModalOpen(false)}
      />
    </AppHeaderWrapper>
  );
};

export default AppHeader;
